import { useEffect, useState } from 'react'

import { Button } from '@/components/ui/button'
import { formatCustomerPackageAmount } from '@/lib/api/customerPackages'
import { cn } from '@/lib/utils'

function balanceLabel(balance) {
  if (balance.service_name) {
    return balance.service_name
  }

  switch (balance.balance_kind) {
    case 'picks':
      return 'Flexible picks'
    case 'credit':
      return 'Wallet credit'
    case 'sessions':
      return 'Sessions'
    default:
      return 'Balance'
  }
}

/**
 * @param {{
 *   open: boolean
 *   pkg: import('@/types/api').CustomerPackageProfile | null
 *   submitting?: boolean
 *   error?: string
 *   onClose: () => void
 *   onSubmit: (payload: Record<string, unknown>) => void | Promise<void>
 * }} props
 */
export function CustomerPackageAdjustModal({
  open,
  pkg,
  submitting = false,
  error = '',
  onClose,
  onSubmit,
}) {
  const [reason, setReason] = useState('')
  const [balances, setBalances] = useState([])
  const [voucherBalance, setVoucherBalance] = useState('')

  useEffect(() => {
    if (!open || !pkg) return

    setReason('')
    setBalances(
      (pkg.balances ?? []).map((row) => ({
        package_balance_id: row.package_balance_id,
        label: balanceLabel(row),
        quantity_initial: row.quantity_initial,
        quantity_remaining: String(row.quantity_remaining ?? ''),
      })),
    )
    setVoucherBalance(
      pkg.gift_voucher_balance_remaining != null
        ? String(pkg.gift_voucher_balance_remaining)
        : '',
    )
  }, [open, pkg])

  if (!open || !pkg) return null

  async function handleSubmit(event) {
    event.preventDefault()

    const payload = {
      reason,
      balances: balances.map((row) => ({
        package_balance_id: row.package_balance_id,
        quantity_remaining: row.quantity_remaining,
      })),
    }

    if (pkg.gift_voucher_balance_remaining != null) {
      payload.gift_voucher_balance_remaining = voucherBalance
    }

    await onSubmit(payload)
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <form
        onSubmit={handleSubmit}
        className="card-surface w-full max-w-lg space-y-4 p-6"
        aria-labelledby="adjust-package-title"
      >
        <div>
          <h2 id="adjust-package-title" className="text-lg font-semibold">
            Adjust package balance
          </h2>
          <p className="mt-1 text-sm text-muted-foreground">
            {pkg.package_name} — manager adjustment is audited.
          </p>
        </div>

        {balances.length > 0 ? (
          <div className="space-y-3">
            {balances.map((row) => (
              <label key={row.package_balance_id} className="block text-sm">
                <span className="mb-1.5 block font-medium">{row.label}</span>
                <div className="flex items-center gap-2">
                  <input
                    type="number"
                    min={0}
                    max={Number(row.quantity_initial)}
                    step="0.01"
                    value={row.quantity_remaining}
                    onChange={(e) => setBalances((current) => current.map((item) => (
                      item.package_balance_id === row.package_balance_id
                        ? { ...item, quantity_remaining: e.target.value }
                        : item
                    )))}
                    className={cn(
                      'h-11 w-full rounded-xl border border-border/80 bg-card px-4 text-sm',
                      'focus:outline-none focus:ring-2 focus:ring-ring',
                    )}
                    required
                  />
                  <span className="shrink-0 text-xs text-muted-foreground">
                    / {row.quantity_initial}
                  </span>
                </div>
              </label>
            ))}
          </div>
        ) : null}

        {pkg.gift_voucher_balance_remaining != null ? (
          <label className="block text-sm">
            <span className="mb-1.5 block font-medium">Voucher balance (AED)</span>
            <input
              type="number"
              min={0}
              step="0.01"
              value={voucherBalance}
              onChange={(e) => setVoucherBalance(e.target.value)}
              className={cn(
                'h-11 w-full rounded-xl border border-border/80 bg-card px-4 text-sm',
                'focus:outline-none focus:ring-2 focus:ring-ring',
              )}
              required
            />
            <span className="mt-1 block text-xs text-muted-foreground">
              Paid {formatCustomerPackageAmount(pkg.purchase_price)}
            </span>
          </label>
        ) : null}

        <label className="block text-sm">
          <span className="mb-1.5 block font-medium">Reason (required)</span>
          <textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={3}
            placeholder="e.g. Goodwill credit after service complaint"
            className={cn(
              'w-full rounded-xl border border-border/80 bg-card px-4 py-3 text-sm',
              'focus:outline-none focus:ring-2 focus:ring-ring',
            )}
            required
            minLength={3}
          />
        </label>

        {error ? (
          <p className="text-sm text-destructive" role="alert">
            {error}
          </p>
        ) : null}

        <div className="flex justify-end gap-2">
          <Button type="button" variant="outline" onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button type="submit" disabled={submitting}>
            {submitting ? 'Saving…' : 'Save adjustment'}
          </Button>
        </div>
      </form>
    </div>
  )
}
